import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type { Wallet } from "../admin-client";
import { useWallets } from "../admin-hooks";
import { ErrorAlert, RequireOperator } from "./guards";
import { Money, ShortID, StatusBadge, when } from "./common";

/**
 * Wallets — every application wallet on the platform, and the two numbers
 * that decide whether a customer can spend.
 *
 * The application column is not a filter that could be turned off, because
 * credit in this platform is held PER APPLICATION and is not transferable.
 * A list that let an operator collapse the application away would invite the
 * sentence "the customer has $40" — which is never true here. They have $40
 * in App A, and nothing in App B, and that distinction is the product.
 */
const PAGE = 50;

const STATUSES = ["active", "frozen", "closed"];

export interface WalletsSearch {
  q?: string;
  status?: string;
}

export function Wallets({ search }: { search: WalletsSearch }) {
  return <RequireOperator>{() => <WalletSearch search={search} />}</RequireOperator>;
}

function WalletSearch({ search }: { search: WalletsSearch }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(search.q ?? "");
  const [status, setStatus] = useState(search.status ?? "");

  // The URL is the source of truth, so back and forward have to pull the
  // inputs along with them.
  useEffect(() => {
    setQ(search.q ?? "");
    setStatus(search.status ?? "");
  }, [search.q, search.status]);

  const wallets = useWallets({ q: search.q, status: search.status, limit: PAGE });
  const rows = wallets.data?.wallets ?? [];
  const drifted = rows.filter((row) => row.cache_stale);

  return (
    <>
      <PageHeader
        title="Wallets"
        description="Prepaid credit, held per application. Balances here are summed from the ledger, not read from the wallet's cache."
      />
      <Card>
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              to: "/wallets",
              search: { q: q.trim() || undefined, status: status || undefined },
            });
          }}
        >
          <Field label="Owner" htmlFor="wallet-q" hint="Email address or organisation name.">
            <Input id="wallet-q" value={q} onChange={(event) => setQ(event.target.value)} />
          </Field>
          <Field label="Status" htmlFor="wallet-status">
            <Select
              id="wallet-status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">Any status</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" busy={wallets.isFetching}>
            Search
          </Button>
        </form>
      </Card>

      <ErrorAlert error={wallets.error} title="Could not load the wallets" />

      {/* Drift is called out above the table rather than left as a column an
          operator has to notice. The cache is written in the same transaction
          as the entries it summarises, so a mismatch is not a stale number —
          it is a bug, and it should interrupt. */}
      {drifted.length > 0 ? (
        <Alert tone="danger" title="A cached balance disagrees with the ledger">
          {drifted.length} wallet{drifted.length === 1 ? "" : "s"} below report a cached balance
          that does not match the sum of their entries. The ledger is the authority; the figures in
          this table are derived from it. Raise a reconciliation before quoting any of these to a
          customer.
        </Alert>
      ) : null}

      <Card title="Wallets">
        {wallets.isPending ? (
          <Spinner label="Loading wallets" />
        ) : (
          <DataTable<Wallet>
            rows={rows}
            rowKey={(row) => row.id}
            empty="No wallet matches that search."
            columns={[
              {
                key: "owner",
                header: "Owner",
                render: (row) => (
                  <>
                    <Link to="/wallets/$walletId" params={{ walletId: row.id }}>
                      {row.owner_label || row.owner_id}
                    </Link>
                    <div className="esm-muted-text">{row.owner_type}</div>
                  </>
                ),
              },
              {
                key: "application",
                header: "Application",
                render: (row) => (
                  <>
                    <code>{row.application_slug}</code>
                    <div className="esm-muted-text">{row.application_name}</div>
                  </>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (row) => <StatusBadge status={row.status} />,
              },
              {
                key: "available",
                header: "Available",
                align: "end",
                render: (row) => <Money value={row.available} />,
              },
              {
                key: "reserved",
                header: "Reserved",
                align: "end",
                // Reserved is shown next to available and never added to it:
                // held funds are not spendable, and a "total" column would be
                // a number the customer cannot use.
                render: (row) => (
                  <>
                    <Money value={row.reserved} />
                    {row.held_reservations > 0 ? (
                      <div className="esm-muted-text">{row.held_reservations} held</div>
                    ) : null}
                  </>
                ),
              },
              {
                key: "cache",
                header: "Cache",
                render: (row) =>
                  row.cache_stale ? (
                    <Badge tone="danger">drifted</Badge>
                  ) : (
                    <Badge tone="neutral">agrees</Badge>
                  ),
              },
              { key: "id", header: "Wallet", render: (row) => <ShortID value={row.id} /> },
              { key: "updated", header: "Last movement", render: (row) => when(row.updated_at) },
            ]}
          />
        )}
      </Card>
    </>
  );
}
